import {
    Auto as AutoSvg,
    Beaker as BeakerSvg,
    Book as BookSvg,
    Briefcase as BriefcaseSvg,
    Calendar as CalendarSvg,
    Charity as CharitySvg,
    Cocktail as CocktailSvg,
    DiscoBall as DiscoBallSvg,
    Fashion as FashionSvg,
    FilmCamera as FilmCameraSvg,
    Game as GameSvg,
    Gear as GearSvg,
    Heart as HeartSvg,
    Holiday as HolidaySvg,
    House as HouseSvg,
    LinkChunky as LinkChunkySvg,
    LogoFacebook as LogoFacebookSvg,
    LogoGooglePlus as LogoGooglePlusSvg,
    LogoInstagram as LogoInstagramSvg,
    LogoLinkedin as LogoLinkedinSvg,
    LogoPinterest as LogoPinterestSvg,
    LogoTwitter as LogoTwitterSvg,
    Mask as MaskSvg,
    Megaphone as MegaphoneSvg,
    MusicNote as MusicNoteSvg,
    Park as ParkSvg,
    Religion as ReligionSvg,
    RockingHorse as RockingHorseSvg,
    Shoe as ShoeSvg,
    Star as StarSvg,
    Travel as TravelSvg,
} from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React from 'react';
import { DecorativeImagePlacement } from '../types/Media';

import {
    ACTIVE_SEASONAL_EVENTBRITE_CATEGORY,
    APPEARANCE_EVENTBRITE_FORMAT,
    ATTRACTION_EVENTBRITE_FORMAT,
    AUTO_BOAT_AIR,
    AUTO_BOAT_AIR_EVENTBRITE_CATEGORY,
    BUSINESS,
    BUSINESS_EVENTBRITE_CATEGORY,
    CHARITY,
    CHARITY_EVENTBRITE_CATEGORY,
    CLASS_EVENTBRITE_FORMAT,
    COMMUNITY,
    COMMUNITY_EVENTBRITE_CATEGORY,
    CONFERENCE_EVENTBRITE_FORMAT,
    CONVENTION_EVENTBRITE_FORMAT,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_FORMAT_MAPPING,
    EXPO_EVENTBRITE_FORMAT,
    FAMILY_EDUCATION,
    FAMILY_EDUCATION_EVENTBRITE_CATEGORY,
    FASHION,
    FASHION_EVENTBRITE_CATEGORY,
    FESTIVAL_EVENTBRITE_FORMAT,
    FILM,
    FILM_EVENTBRITE_CATEGORY,
    FOOD,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    FORMATTED_SUBCATEGORY_MAP,
    GALA_EVENTBRITE_FORMAT,
    GAME_EVENTBRITE_FORMAT,
    HEALTH,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES,
    HOBBIES_EVENTBRITE_CATEGORY,
    HOME_LIFESTYLE,
    HOME_LIFESTYLE_EVENTBRITE_CATEGORY,
    MUSIC,
    MUSIC_EVENTBRITE_CATEGORY,
    NETWORKING_EVENTBRITE_FORMAT,
    NIGHTLIFE,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    OTHER,
    OTHER_EVENTBRITE_CATEGORY,
    PARTY_EVENTBRITE_FORMAT,
    PERFORMANCE_EVENTBRITE_FORMAT,
    PERFORMING,
    PERFORMING_EVENTBRITE_CATEGORY,
    POLITICS,
    POLITICS_EVENTBRITE_CATEGORY,
    RACE_EVENTBRITE_FORMAT,
    RALLY_EVENTBRITE_FORMAT,
    RELIGION,
    RELIGION_EVENTBRITE_CATEGORY,
    RETREAT_EVENTBRITE_FORMAT,
    SCHOOL,
    SCHOOL_EVENTBRITE_CATEGORY,
    SCIENCE,
    SCIENCE_EVENTBRITE_CATEGORY,
    SCREENING_EVENTBRITE_FORMAT,
    SEASONAL,
    SEASONAL_EVENTBRITE_CATEGORY,
    SEMINAR_EVENTBRITE_FORMAT,
    SPORTS,
    SPORTS_EVENTBRITE_CATEGORY,
    TOURNAMENT_EVENTBRITE_FORMAT,
    TOUR_EVENTBRITE_FORMAT,
    TRAVEL,
    TRAVEL_EVENTBRITE_CATEGORY,
} from '@eventbrite/categories';
import { BucketContentType, BucketEntityType } from '../types';

export const EVENT_TYPE = 'event';
export const EVENTS_TYPE = 'events';
export const ALL_EVENTS_TYPE = 'all events';
export const GOOGLE_MAPS_CLIENT_KEY = 'gme-eventbrite';

export const EVENTBRITE = 'Eventbrite';
export const TICKETFLY = 'Ticketfly';
export const GOLDSTAR = 'Goldstar';

export const LINK_FOLLOW_ALLOWLIST = [EVENTBRITE, TICKETFLY];

/****************************** TEMPLATES AND CARD TYPES ********************************/

export const EVENT_CARD_TYPE = EVENT_TYPE;
export const ARTICLE_CARD_TYPE = 'article';
export const PROFILE_CARD_TYPE = 'profile';
export const CATEGORY_TYPE = 'category';
export const NEARBY_CITY_TYPE = 'place';
export const TRENDING_SEARCH_TYPE = 'trending';
export const COLLECTION_TYPE = 'collection';
export const EVENT_TIME_OPTIONS_TYPE = 'event_time_options';

export const ENTITY_TYPE_MAP: Record<BucketEntityType, BucketContentType> = {
    [EVENT_CARD_TYPE]: 'events',
    [ARTICLE_CARD_TYPE]: 'articles',
    [CATEGORY_TYPE]: 'tags',
    [NEARBY_CITY_TYPE]: 'places',
    [TRENDING_SEARCH_TYPE]: 'trending',
    [COLLECTION_TYPE]: 'collections',
};

export const SINGULARIZED_ENTITY_TYPE_MAP: { [index: string]: string } = {
    events: EVENT_CARD_TYPE,
    promoted_results: EVENT_CARD_TYPE,
    top_match: EVENT_CARD_TYPE,
    profiles: PROFILE_CARD_TYPE,
    articles: ARTICLE_CARD_TYPE,
    places: NEARBY_CITY_TYPE,
};

/****************************** SEARCH CONSTANTS ********************************/

export const SEARCH_CRITERIA_TYPE_MAP = [
    'event_search',
    'article_search',
    'profile_search',
];
export const SEARCH_TYPE_MAP = ['events', 'articles', 'profiles'];

export const START_DATE = 'start_date';
export const END_DATE = 'end_date';
export const EVENT_PAGE_PARAM = 'page';
export const SEARCH_TYPE_PAGE_PARAM_MAP = {
    [EVENT_CARD_TYPE]: EVENT_PAGE_PARAM,
    [ARTICLE_CARD_TYPE]: 'a_page',
    [PROFILE_CARD_TYPE]: 'p_page',
};

/****************************** CATEGORY MAPPING ********************************/

export const CATEGORY_TAG_TYPE = 'Category';

// We want a different order for search with most popular filters at the top
export const EVENTBRITE_CATEGORY_MAPPING_SEARCH = {
    [BUSINESS_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[BUSINESS_EVENTBRITE_CATEGORY],
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
    [HEALTH_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[HEALTH_EVENTBRITE_CATEGORY],
    [MUSIC_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[MUSIC_EVENTBRITE_CATEGORY],
    [AUTO_BOAT_AIR_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[AUTO_BOAT_AIR_EVENTBRITE_CATEGORY],
    [CHARITY_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[CHARITY_EVENTBRITE_CATEGORY],
    [COMMUNITY_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[COMMUNITY_EVENTBRITE_CATEGORY],
    [FAMILY_EDUCATION_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[FAMILY_EDUCATION_EVENTBRITE_CATEGORY],
    [FASHION_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[FASHION_EVENTBRITE_CATEGORY],
    [FILM_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[FILM_EVENTBRITE_CATEGORY],
    [HOBBIES_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[HOBBIES_EVENTBRITE_CATEGORY],
    [HOME_LIFESTYLE_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[HOME_LIFESTYLE_EVENTBRITE_CATEGORY],
    [PERFORMING_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[PERFORMING_EVENTBRITE_CATEGORY],
    [POLITICS_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[POLITICS_EVENTBRITE_CATEGORY],
    [RELIGION_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[RELIGION_EVENTBRITE_CATEGORY],
    [SCHOOL_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[SCHOOL_EVENTBRITE_CATEGORY],
    [SCIENCE_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[SCIENCE_EVENTBRITE_CATEGORY],
    [SEASONAL_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[SEASONAL_EVENTBRITE_CATEGORY],
    [SPORTS_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[SPORTS_EVENTBRITE_CATEGORY],
    [TRAVEL_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[TRAVEL_EVENTBRITE_CATEGORY],
    [OTHER_EVENTBRITE_CATEGORY]:
        EVENTBRITE_CATEGORY_MAPPING[OTHER_EVENTBRITE_CATEGORY],
};

// Translators: these are going to be used as part of a sentence
export const EVENTBRITE_CATEGORY_LOWERCASE_MAPPING = {
    [BUSINESS_EVENTBRITE_CATEGORY]: gettext('business'),
    [SCIENCE_EVENTBRITE_CATEGORY]: gettext('science & tech'),
    [MUSIC_EVENTBRITE_CATEGORY]: gettext('music'),
    [FILM_EVENTBRITE_CATEGORY]: gettext('film & media'),
    [PERFORMING_EVENTBRITE_CATEGORY]: gettext('arts'),
    [FASHION_EVENTBRITE_CATEGORY]: gettext('fashion'),
    [HEALTH_EVENTBRITE_CATEGORY]: gettext('health'),
    [SPORTS_EVENTBRITE_CATEGORY]: gettext('sports & fitness'),
    [TRAVEL_EVENTBRITE_CATEGORY]: gettext('travel & outdoor'),
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: gettext('food & drink'),
    [CHARITY_EVENTBRITE_CATEGORY]: gettext('charity & causes'),
    [POLITICS_EVENTBRITE_CATEGORY]: gettext('government'),
    [COMMUNITY_EVENTBRITE_CATEGORY]: gettext('community'),
    [RELIGION_EVENTBRITE_CATEGORY]: gettext('spirituality'),
    [FAMILY_EDUCATION_EVENTBRITE_CATEGORY]: gettext('family & education'),
    [SEASONAL_EVENTBRITE_CATEGORY]: gettext('holiday'),
    [HOME_LIFESTYLE_EVENTBRITE_CATEGORY]: gettext('home & lifestyle'),
    [AUTO_BOAT_AIR_EVENTBRITE_CATEGORY]: gettext('auto, boat & air'),
    [HOBBIES_EVENTBRITE_CATEGORY]: gettext('hobbies'),
    [SCHOOL_EVENTBRITE_CATEGORY]: gettext('school activities'),
    [OTHER_EVENTBRITE_CATEGORY]: gettext('other'),
};

/****************************** DATES MAPPING ****************************/

const TODAY = 'today';
const TOMORROW = 'tomorrow';
const THIS_MONTH = 'this_month';
const THIS_WEEK = 'this_week';
const THIS_WEEKEND = 'this_weekend';
const NEXT_WEEK = 'next_week';
const NEXT_MONTH = 'next_month';

export const DATE_TAG = 'date';

// only used as default value, DON'T use it on the dropdowns (don't add it to DATES_MAPPING)
export const CURRENT_FUTURE = 'current_future';

export const CUSTOM_DATE = 'custom_date';

export const DATES_MAPPING = {
    [TODAY]: gettext('Today'),
    [TOMORROW]: gettext('Tomorrow'),
    [THIS_WEEKEND]: gettext('This weekend'),
    [THIS_WEEK]: gettext('This week'),
    [NEXT_WEEK]: gettext('Next week'),
    [THIS_MONTH]: gettext('This month'),
    [NEXT_MONTH]: gettext('Next month'),
    [CUSTOM_DATE]: gettext('Pick a date...'),
};

// We want a different order for search with most popular filters at the top
export const DATES_MAPPING_SEARCH = {
    [TODAY]: DATES_MAPPING[TODAY],
    [TOMORROW]: DATES_MAPPING[TOMORROW],
    [THIS_WEEKEND]: DATES_MAPPING[THIS_WEEKEND],
    [CUSTOM_DATE]: DATES_MAPPING[CUSTOM_DATE],
    [THIS_WEEK]: DATES_MAPPING[THIS_WEEK],
    [NEXT_WEEK]: DATES_MAPPING[NEXT_WEEK],
    [THIS_MONTH]: DATES_MAPPING[THIS_MONTH],
    [NEXT_MONTH]: DATES_MAPPING[NEXT_MONTH],
};

export const DATES_UPPERCASE_MAPPING = {
    [TODAY]: gettext('Today'),
    [TOMORROW]: gettext('Tomorrow'),
    [THIS_WEEKEND]: gettext('This Weekend'),
    [THIS_WEEK]: gettext('This Week'),
    [NEXT_WEEK]: gettext('Next Week'),
    [THIS_MONTH]: gettext('This Month'),
    [NEXT_MONTH]: gettext('Next Month'),
    [CUSTOM_DATE]: gettext('Pick a date...'),
};

// Translators: these are going to be used as part of a sentence
export const DATES_LOWERCASE_MAPPING = {
    [TODAY]: gettext('today'),
    [TOMORROW]: gettext('tomorrow'),
    [THIS_WEEKEND]: gettext('this weekend'),
    [THIS_WEEK]: gettext('this week'),
    [NEXT_WEEK]: gettext('next week'),
    [THIS_MONTH]: gettext('this month'),
    [NEXT_MONTH]: gettext('next month'),
};

export const DATES_TO_REFINEMENT = {
    [TODAY]: 'today',
    [TOMORROW]: 'tomorrow',
    [THIS_WEEKEND]: 'this-weekend',
    [THIS_WEEK]: 'this-week',
    [NEXT_WEEK]: 'next-week',
    [THIS_MONTH]: 'this-month',
    [NEXT_MONTH]: 'next-month',
};

/****************************** FORMAT MAPPING ****************************/

export const FORMAT_TAG_TYPE = 'Format';

// We want a different order for search with most popular filters at the top
export const EVENTBRITE_FORMAT_MAPPING_SEARCH = {
    [CLASS_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[CLASS_EVENTBRITE_FORMAT],
    [CONFERENCE_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[CONFERENCE_EVENTBRITE_FORMAT],
    [FESTIVAL_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[FESTIVAL_EVENTBRITE_FORMAT],
    [PARTY_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[PARTY_EVENTBRITE_FORMAT],
    [APPEARANCE_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[APPEARANCE_EVENTBRITE_FORMAT],
    [ATTRACTION_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[ATTRACTION_EVENTBRITE_FORMAT],
    [CONVENTION_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[CONVENTION_EVENTBRITE_FORMAT],
    [EXPO_EVENTBRITE_FORMAT]: EVENTBRITE_FORMAT_MAPPING[EXPO_EVENTBRITE_FORMAT],
    [GALA_EVENTBRITE_FORMAT]: EVENTBRITE_FORMAT_MAPPING[GALA_EVENTBRITE_FORMAT],
    [GAME_EVENTBRITE_FORMAT]: EVENTBRITE_FORMAT_MAPPING[GAME_EVENTBRITE_FORMAT],
    [NETWORKING_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[NETWORKING_EVENTBRITE_FORMAT],
    [PERFORMANCE_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[PERFORMANCE_EVENTBRITE_FORMAT],
    [RACE_EVENTBRITE_FORMAT]: EVENTBRITE_FORMAT_MAPPING[RACE_EVENTBRITE_FORMAT],
    [RALLY_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[RALLY_EVENTBRITE_FORMAT],
    [RETREAT_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[RETREAT_EVENTBRITE_FORMAT],
    [SCREENING_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[SCREENING_EVENTBRITE_FORMAT],
    [SEMINAR_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[SEMINAR_EVENTBRITE_FORMAT],
    [TOURNAMENT_EVENTBRITE_FORMAT]:
        EVENTBRITE_FORMAT_MAPPING[TOURNAMENT_EVENTBRITE_FORMAT],
    [TOUR_EVENTBRITE_FORMAT]: EVENTBRITE_FORMAT_MAPPING[TOUR_EVENTBRITE_FORMAT],
};

/****************************** PRICE MAPPING ****************************/

const FREE = 'free';
const PAID = 'paid';

export const PRICE_MAPPING = {
    [FREE]: gettext('Free'),
    [PAID]: gettext('Paid'),
};

/****************************** SORT MAPPING ****************************/

export const QUALITY = 'quality';

/****************************** SELECT FIELD CONSTANTS ********************************/
/*
 Receives a data map where the keys correlate to actual value, and the
 value correlates to what should be displayed. Reformats into a structure
 suitable for the <SelectField />

 @param dataMap - object
 {'this_week': 'This week', 'this_month': 'This month'}

 @return array
 [
    {value: 'this_week', display: 'This week'},
    {value: 'this_month', display: 'This month'}
]
*/
const _formatMapper = (
    dataMap: { [key: string]: string },
    valueKey: string,
    displayKey: string,
) => {
    return Object.keys(dataMap).reduce((memo: object[], key: string) => {
        const val = dataMap[key];

        return [...memo, { [valueKey]: key, [displayKey]: val }];
    }, []);
};

const _formatMappingForSelectField = (dataMap: any) =>
    _formatMapper(dataMap, 'value', 'display');

export const FORMATTED_SUBCATEGORY_MAPPING = _formatMappingForSelectField(
    Object.fromEntries(
        Object.entries(FORMATTED_SUBCATEGORY_MAP).map(([key, subcategory]) => {
            return [key, subcategory.title];
        }),
    ),
);
export const FORMATTED_CATEGORY_MAPPING = _formatMappingForSelectField(
    EVENTBRITE_CATEGORY_MAPPING,
);
export const FORMATTED_FORMAT_MAPPING = _formatMappingForSelectField(
    EVENTBRITE_FORMAT_MAPPING,
);
export const FORMATTED_DATE_MAPPING =
    _formatMappingForSelectField(DATES_MAPPING);
export const FORMATTED_PRICE_MAPPING =
    _formatMappingForSelectField(PRICE_MAPPING);

/****************************** COPY ********************************/

export const DEFAULT_BLURB_NO_BUCKETS = gettext(
    "Looks like we don't have any current events listed nearby. Try searching another city.",
);
export const DEFAULT_BLURB_NO_EVENTS = gettext(
    "Looks like we don't have any current events listed, let's see what's going on in nearby cities.",
);
export const getDefaultBlurb = (city: string) =>
    gettext(
        "Looking for something to do in %(city)s? Whether you're a local, new in town or just cruising through we've got loads of great tips and events. You can explore by location, what's popular, our top picks, free stuff... you got this. Ready?",
        { city },
    );

export const EVENT_SAVE = gettext('Event has been saved successfully.');
export const EVENT_SAVE_ERROR = gettext(
    'There was a problem saving the event, please try again.',
);

/****************************** City Browse Location Constants ********************************/

export const CITY_CUTOFF = 6;
export const CITY_NAME_SHORT_CUT = 2;
export const CITY_NAME_DEFAULT_CUT = 4;
export const CITY_GRADIENTS = ['eds-bg-color--ui-orange'];
export const SHORT_STATES = {
    alabama: 'AL',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    'district of columbia': 'DC',
    florida: 'FL',
    georgia: 'GA',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY',
};

/****************************** COOKIES CONSTANTS ********************************/

export const EVENT_COOKIE = 'ebEventId';

/****************************** GA ******************************/

export const GA_USER_CURRENT_LOCATION_ACTION = 'UseCurrentLocation';
export const GA_USER_CURRENT_LOCATION_DENIED_LABEL = 'access denied';
export const GA_USER_CURRENT_LOCATION_ERROR_LABEL = 'location error';

//  This constants are used to track the different type of suggestions listed on the global header
export const SUGGESTION_TYPE_HISTORY = 'recent';
export const SUGGESTION_TYPE_POPULAR = 'popular';

/****************************** Current Location ******************************/

export const LOCATION_DIALOG_CONTENT = {
    headerIconType: <GearSvg />,
    title: gettext('Location Access Blocked'),
    message: gettext(
        'We are unable to resolve your current location. Please check your browser and system permissions and try again.',
    ),
    primaryText: gettext('Okay'),
    primaryType: 'link',
};

export const CATEGORY_LONG_NAME_ICON_MAP = {
    [BUSINESS_EVENTBRITE_CATEGORY]: <BriefcaseSvg />,
    [MUSIC_EVENTBRITE_CATEGORY]: <MusicNoteSvg />,
    [NIGHTLIFE_EVENTBRITE_CATEGORY]: (
        <DiscoBallSvg {...{ viewBox: '0 0 47 48' }} />
    ),
    [PERFORMING_EVENTBRITE_CATEGORY]: <MaskSvg />,
    [SCIENCE_EVENTBRITE_CATEGORY]: <BeakerSvg />,
    [COMMUNITY_EVENTBRITE_CATEGORY]: <ParkSvg />,
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: <CocktailSvg />,
    [FILM_EVENTBRITE_CATEGORY]: <FilmCameraSvg />,
    [SPORTS_EVENTBRITE_CATEGORY]: <ShoeSvg />,
    [HEALTH_EVENTBRITE_CATEGORY]: <HeartSvg />,
    [TRAVEL_EVENTBRITE_CATEGORY]: <TravelSvg />,
    [CHARITY_EVENTBRITE_CATEGORY]: <CharitySvg />,
    [RELIGION_EVENTBRITE_CATEGORY]: <ReligionSvg />,
    [FAMILY_EDUCATION_EVENTBRITE_CATEGORY]: <RockingHorseSvg />,
    [SEASONAL_EVENTBRITE_CATEGORY]: <HolidaySvg />,
    [SCHOOL_EVENTBRITE_CATEGORY]: <BookSvg />,
    [ACTIVE_SEASONAL_EVENTBRITE_CATEGORY]: <CalendarSvg />,
    //Backup: 'trash'
    [POLITICS_EVENTBRITE_CATEGORY]: <MegaphoneSvg />,
    [FASHION_EVENTBRITE_CATEGORY]: <FashionSvg />,
    [HOME_LIFESTYLE_EVENTBRITE_CATEGORY]: <HouseSvg />,
    [AUTO_BOAT_AIR_EVENTBRITE_CATEGORY]: <AutoSvg />,
    [HOBBIES_EVENTBRITE_CATEGORY]: <GameSvg />,
    [OTHER_EVENTBRITE_CATEGORY]: <StarSvg />,
};

export const MOBILE_BREAKPOINT = '822px';

/****************************** SHARED CLASSES ********************************/

export const SHARED_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-vert-16',
);

export const NON_EVENT_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-top-16',
    'eds-l-sw-pad-vert-16',
    'eds-l-sm-pad-vert-16',
    'eds-l-sn-pad-vert-16',
);

export const BUCKET_WRAPPER_WITH_DECORATION_CLASSES = classNames(
    'eds-l-mar-top-5',
    'eds-l-sm-mar-top-16',
    'eds-l-sn-mar-top-16',
);

export const TAG_BACKGROUND_COLOR = 'eds-bg-color--ui-blue';
export const TAG_STYLE = 'outlined';

/****************************** TEMPLATES AND CARD TYPES ********************************/

export const ARTICLE_TEMPLATE = 'article';
export const CALENDAR_TEMPLATE = 'calendar';
export const FEATURED_TEMPLATE = 'featured';
export const CATEGORY_TEMPLATE = 'category';
export const MAP_TEMPLATE = 'map';
export const NEARBY_CITIES_TEMPLATE = 'nearby_place';
export const TRENDING_SEARCHES_TEMPLATE = 'trending_searches';
export const COLLECTION_TEMPLATE = 'collection';
export const ORGANIZER_FOLLOW_TEMPLATE = 'organizer_follow';
export const EVENT_TIME_OPTIONS_TEMPLATE = 'event_time_options';

export const LARGE_CARD_TEMPLATE_3 = 'card_big_3';
export const LARGE_CARD_TEMPLATE_6 = 'card_big_6';
export const LIST_CARD_TEMPLATE = 'card_list';
export const CARD_TEMPLATE_4 = 'card_small_4';
export const CARD_TEMPLATE_8 = 'card_small_8';
export const CARD_BIG = 'card_big';

const THIS_WEEKEND_BUCKETS_KEY = 'this_weekend';
const POPULAR_EVENTS_BUCKET_KEY = 'popular_events';
const CURATED_EVENTS_BUCKET_KEY = 'best_of_city_events';
const MUSIC_EVENTS_BUCKET_KEY = 'music_events';

export const EDITORIAL_ARTICLES_BUCKET_KEY = 'editorial_articles';
export const FREE_EVENTS_BUCKET_KEY = 'free_events';

/****************************** URLS ********************************/

export const BASE_URL = '/d';
export const BROWSE_URL = ':location/events/';
export const SEARCH_URL = ':location/:refinementOne(/:refinementTwo)/';

export const SEARCH_RELEVANT_QUERY_PARAMS = [
    START_DATE,
    END_DATE,
    EVENT_PAGE_PARAM,
    'src',
    'bbox',
    'subcategories',
    'cur',
    'lang',
    'hash',
    'followed_orgs',
    'organization',
];

/****************************** CATEGORY ID MAPPING ********************************/

//Very TBD, will be replaced once official
//category graphics are put together.
export const CATEGORY_NAME_ICON_MAP: { [key: number]: React.ReactNode } = {
    [BUSINESS]: <BriefcaseSvg />,
    [MUSIC]: <MusicNoteSvg />,
    [NIGHTLIFE]: <DiscoBallSvg />,
    [PERFORMING]: <MaskSvg />,
    [SCIENCE]: <BeakerSvg />,
    [COMMUNITY]: <ParkSvg />,
    [FOOD]: <CocktailSvg />,
    [FILM]: <FilmCameraSvg />,
    [SPORTS]: <ShoeSvg />,
    [HEALTH]: <HeartSvg />,
    [TRAVEL]: <TravelSvg />,
    [CHARITY]: <CharitySvg />,
    [RELIGION]: <ReligionSvg />,
    [FAMILY_EDUCATION]: <RockingHorseSvg />,
    [SEASONAL]: <HolidaySvg />,
    [SCHOOL]: <BookSvg />,
    //Backup: 'trash'
    [POLITICS]: <MegaphoneSvg />,
    [FASHION]: <FashionSvg />,
    [HOME_LIFESTYLE]: <HouseSvg />,
    [AUTO_BOAT_AIR]: <AutoSvg />,
    [HOBBIES]: <GameSvg />,
    [OTHER]: <StarSvg />,
};

export const CATEGORY_DROPDOWN_SELECT = 'category_select';

/****************************** IMAGES ********************************/

export const DECORATIVE_IMAGE_PLACEMENT_MAP: DecorativeImagePlacement[] = [
    {
        selectors: [POPULAR_EVENTS_BUCKET_KEY, CURATED_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationTopRightUrl',
        position: 'right',
        classes: 'decorative-image--one',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [THIS_WEEKEND_BUCKETS_KEY],
        urlSelector: 'decorationTopLeftUrl',
        position: 'left',
        classes: 'decorative-image--two',
        parentClasses: 'eds-l-pad-top-12',
    },
    {
        selectors: [EDITORIAL_ARTICLES_BUCKET_KEY, MUSIC_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomRightUrl',
        position: 'right',
        classes: 'decorative-image--three',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [FREE_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomLeftUrl',
        position: 'left',
        classes: 'decorative-image--four',
        parentClasses: 'eds-l-pad-top-6',
    },
];

/****************************** Artist Social Link Tye Constants ********************************/

export const SOCIAL_TYPE_ICON_MAP: { [key: string]: React.ReactElement } = {
    instagram: <LogoInstagramSvg />,
    twitter: <LogoTwitterSvg />,
    linkedin: <LogoLinkedinSvg />,
    facebook: <LogoFacebookSvg />,
    pinterest: <LogoPinterestSvg />,
    'google-plus': <LogoGooglePlusSvg />,
    web: <LinkChunkySvg />,
};

export const HEADER_HEIGHT = 72;

export { FESTIVAL_EVENTBRITE_FORMAT };
